import React, { useEffect } from "react"
import { useIntl } from "react-intl"
import { Button, CurrencyIcon } from "@btc-alpha/ui-components"

import commonMessages from "messages/common"
import financeMessages from "messages/finance"
import { TDefaultModalProps } from "hooks/useModal"
import { FORMAT_NUMBER_OPTIONS_USD } from "constants/format"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { MAX_PRICE_PRECISION, MIN_PRICE_PRECISION } from "utils/constants"
import { TAsset } from "./types"

import styles from "./wallets.module.scss"

const getFormatNumberOptions = (precision: number | null) => ({
	useGrouping: true,
	minimumFractionDigits: Math.min(MIN_PRICE_PRECISION, precision || MAX_PRICE_PRECISION),
	maximumFractionDigits: precision || MAX_PRICE_PRECISION,
})

const WalletsDetails: React.FC<
	TDefaultModalProps &
		TAsset & {
			rate: number
			onDeposit?: (code: string) => void
			onWithdraw?: (code: string) => void
			onTransfer?: (code: string) => void
			onTrade?: () => void
		}
> = ({
	code,
	rate = 1,
	balance,
	available,
	precision,
	reserve,
	is_deposit_enabled,
	is_withdraw_enabled,
	updateModal,
	onDeposit,
	onWithdraw,
	onTransfer,
	onTrade,
	closeModal,
}) => {
	const { formatNumber, formatMessage } = useIntl()

	useEffect(() => {
		const customTitle = (
			<>
				<button type="button" className={styles.modalInfoButton} id={styles.modalInfoButton}>
					<InfoIcon />
				</button>

				<div className={styles.modalHeaderCurrency}>
					<CurrencyIcon name={code} size={20} />
					<span>{code}</span>
				</div>
			</>
		)

		updateModal({ title: customTitle })
	}, [code, updateModal])

	const renderConverted = (value: string | number) =>
		`≈ $${formatNumber(+value * (rate || 0), FORMAT_NUMBER_OPTIONS_USD)}`

	const handleDeposit = () => {
		if (onDeposit) {
			closeModal()
			setTimeout(() => onDeposit(code), 0)
		}
	}

	const handleWithdraw = () => {
		if (onWithdraw) {
			closeModal()
			setTimeout(() => onWithdraw(code), 0)
		}
	}

	const handleTransfer = () => {
		if (onTransfer) {
			closeModal()
			setTimeout(() => onTransfer(code), 0)
		}
	}

	const handleTrade = () => {
		if ( onTrade) {
			closeModal()
			setTimeout(() => onTrade(), 0)
		}
	}

	return (
		<>
			<div className={styles.modalAmountTitle}>
				<strong>{formatMessage(financeMessages.available)}</strong>
				<span>{formatNumber(available, getFormatNumberOptions(precision))}</span>
				{renderConverted(available)}
			</div>

			<div className={styles.modalDetailsTable}>
				<div>
					<div>{formatMessage(financeMessages.equity)}</div>
					<div>{formatNumber(+balance, getFormatNumberOptions(precision))}</div>
					<div>{renderConverted(balance)}</div>
				</div>
				<div>
					<div>{formatMessage(financeMessages.frozen)}</div>
					<div>{formatNumber(+reserve, getFormatNumberOptions(precision))}</div>
					<div>{renderConverted(reserve)}</div>
				</div>
			</div>

			<footer>
				<Button
					caption={formatMessage(commonMessages.deposit)}
					icon="ai-credit-card-upload"
					size="mini"
					disabled={!is_deposit_enabled}
					onClick={handleDeposit}
				/>

				<Button
					caption={formatMessage(commonMessages.withdraw)}
					icon="ai-credit-card-download"
					size="mini"
					kind="outlined"
					disabled={!available || !is_withdraw_enabled}
					onClick={handleWithdraw}
				/>

				<Button
					title={formatMessage(commonMessages.transfer)}
					size="mini"
					kind="outlined"
					icon="ai-switch-horizontal-01"
					disabled={!available}
					onClick={handleTransfer}
				/>
				<Button
					title={formatMessage(commonMessages.trade)}
					size="mini"
					kind="outlined"
					icon="ai ai-trade-candle-01"
					onClick={handleTrade}
				/>
			</footer>
		</>
	)
}

export default WalletsDetails
